import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

interface PagesDistributionChartProps {
  pages: Record<string, number>;
  isLoading?: boolean;
}

const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#6366F1', '#EC4899', '#14B8A6'];

export function PagesDistributionChart({ pages, isLoading }: PagesDistributionChartProps) {
  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="h-4 bg-gray-200 rounded animate-pulse mb-6"></div>
        <div className="h-80 bg-gray-100 rounded animate-pulse"></div>
      </div>
    );
  }

  // Process data for charts
  const pageData = Object.entries(pages)
    .sort((a, b) => b[1] - a[1])
    .map(([name, votes], index) => ({
      name: name.length > 30 ? `${name.substring(0, 30)}...` : name,
      fullName: name,
      votes,
      color: COLORS[index % COLORS.length],
      percentage: 0 // Will be calculated below
    }));

  const totalVotes = pageData.reduce((sum, item) => sum + item.votes, 0);
  
  // Calculate percentages
  pageData.forEach(item => {
    item.percentage = totalVotes > 0 ? (item.votes / totalVotes) * 100 : 0;
  });

  const topPages = pageData.slice(0, 10);
  const pieData = pageData.slice(0, 6);

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 max-w-xs">
          <p className="font-semibold text-gray-900 text-sm break-words">{data.fullName || data.name}</p>
          <p className="text-blue-600 text-sm">
            <span className="inline-block w-3 h-3 bg-blue-500 rounded-full mr-2"></span>
            Votes: {data.votes.toLocaleString()} ({data.percentage.toFixed(1)}%)
          </p>
        </div>
      );
    }
    return null;
  };

  const PieTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 max-w-xs">
          <p className="font-semibold text-gray-900 text-sm break-words">{data.fullName || data.name}</p>
          <p style={{ color: data.color }} className="text-sm">
            <span className="inline-block w-3 h-3 rounded-full mr-2" style={{ backgroundColor: data.color }}></span>
            {data.votes.toLocaleString()} votes ({data.percentage.toFixed(1)}%)
          </p>
        </div>
      );
    }
    return null;
  };

  const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }: any) => {
    if (percent < 0.05) return null; // Don't show labels for slices smaller than 5%
    
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text 
        x={x} 
        y={y} 
        fill="white" 
        textAnchor={x > cx ? 'start' : 'end'} 
        dominantBaseline="central"
        fontSize={12}
        fontWeight="bold"
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Page Performance</h3>
          <p className="text-sm text-gray-600">Most active pages by feedback volume</p>
        </div>
        <div className="text-right">
          <div className="text-2xl font-bold text-purple-600">📄</div>
          <div className="text-xs text-gray-500 mt-1">
            {Object.keys(pages).length} pages
          </div>
        </div>
      </div>

      {pageData.length > 0 ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Pie Chart */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-gray-700 mb-3">Distribution Overview</h4>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={renderCustomizedLabel}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="votes"
                  animationBegin={0}
                  animationDuration={800}
                >
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip content={<PieTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Bar Chart */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-gray-700 mb-3">Top 10 Pages</h4>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={topPages} layout="horizontal" margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis type="number" tick={{ fontSize: 12, fill: '#6B7280' }} />
                <YAxis 
                  type="category" 
                  dataKey="name" 
                  tick={{ fontSize: 11, fill: '#6B7280' }}
                  width={120}
                />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="votes" radius={[0, 4, 4, 0]}>
                  {topPages.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      ) : (
        <div className="text-center py-12">
          <div className="text-4xl mb-4">📄</div>
          <p className="text-gray-500">No page data available yet</p>
          <p className="text-sm text-gray-400">Data will appear as feedback is received from different pages</p>
        </div>
      )}

      {/* Summary Statistics */}
      {pageData.length > 0 && (
        <div className="mt-6 pt-6 border-t border-gray-100">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-lg font-bold text-blue-600">{pageData.length}</div>
              <div className="text-xs text-gray-500">Total Pages</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold text-green-600">{totalVotes.toLocaleString()}</div>
              <div className="text-xs text-gray-500">Total Votes</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold text-purple-600">
                {pageData.length > 0 ? Math.round(totalVotes / pageData.length) : 0}
              </div>
              <div className="text-xs text-gray-500">Avg per Page</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-bold text-orange-600">
                {pageData[0]?.percentage.toFixed(1) || 0}%
              </div>
              <div className="text-xs text-gray-500">Top Page</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 